
/**
 * Type definitions for logger functionality
 */

// Basic log options interface for logger context
export interface LogOptions {
  errorMessage?: string;
  eventDetails?: any;
  url?: string;
  component?: string;
  context?: Record<string, any>;
  userReported?: boolean;
  timestamp?: Date;
  code?: string;
  tags?: string[];
  [key: string]: any; // Allow additional properties
}

// For compatibility with the error method in logger
export class LogError extends Error {
  options?: LogOptions;
  
  constructor(message: string, options?: LogOptions) {
    super(message);
    this.name = 'LogError';
    this.options = options;
  }
}

// Helper function to create a LogError from LogOptions
export function createLogError(message: string, options?: LogOptions): LogError {
  return new LogError(message, options);
}

// OpenAI-specific log options for better error tracking
export interface OpenAILogOptions extends LogOptions {
  model?: string;
  tokenUsage?: number;
  operation?: 'embedding' | 'completion' | 'chat' | 'batch';
  batchSize?: number;
  retryCount?: number;
}

// Crawler-specific log options
export interface CrawlerLogOptions extends LogOptions {
  url?: string;
  proxyUrl?: string;
  pagesProcessed?: number;
  totalPages?: number;
  stage?: string;
  duration?: number;
  sitemapFound?: boolean;
  robotsTxtFound?: boolean;
  statusCode?: number;
  responseSize?: number;
}

// Sitemap URL log for tracking crawling of sitemap URLs
export interface SitemapUrlLog {
  url: string;
  success: boolean;
  timestamp: Date;
  errorMessage?: string;
  statusCode?: number;
  responseSize?: number;
}

// Sitemap stats interface for aggregate reporting
export interface SitemapStats {
  total: number;
  successful: number;
  failed: number;
  successRate: number;
  failedUrls: Array<{url: string, error: string}>;
  statusCodes: Record<string, number>;
  averageResponseSize: number;
  crawlTime: number;
}

// Helper function to create OpenAI-specific log error
export function createOpenAILogError(message: string, options?: OpenAILogOptions): LogError {
  return new LogError(message, options);
}

// Helper function to create Crawler-specific log error
export function createCrawlerLogError(message: string, options?: CrawlerLogOptions): LogError {
  return new LogError(message, {
    ...options,
    component: 'CrawlerService',
    tags: [...(options?.tags || []), 'crawler']
  });
}

// Error types for categorization
export type ErrorCategory = 'api' | 'storage' | 'processing' | 'auth' | 'network' | 'unknown';

// Log entry for structured logging
export interface LogEntry {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: Date;
  category?: ErrorCategory;
  context?: Record<string, any>;
  stackTrace?: string;
  tags?: string[];
}
